import { Link, useLocation } from "react-router-dom";
import { useAuthUser } from "@/lib/auth-context";
import type { CourseManifest } from "@/lib/course";
import type { ProgressRecord } from "@/lib/progress";
import { coursePercent } from "@/lib/progress";
import { CapMark } from "@/components/icons";
import { AdminChip } from "./AdminChip";
import { UserMenu } from "./UserMenu";
import { SubNav } from "./SubNav";

// Tailwind breakpoints in play:
//   default (<640px)  — phones. Show only essentials: hamburger button,
//                       lesson chip (if in-course), brand logo, user menu.
//   sm (>=640px)      — small tablets. Add course title / "Summer Internship" text.
//   md (>=768px)      — large tablets. Add chapter breadcrumb chunk + percent.
//   lg (>=1024px)     — desktops. Full row: progress bar, brand name, user name.

interface Props {
  // Course-context (all optional — present on lesson pages, absent on home/etc)
  manifest?: CourseManifest;
  progress?: ProgressRecord | null;
  chapterNumber?: number;
  chapterTitle?: string;
  lessonNumber?: number;
  lessonTitle?: string;
  // Drawer wiring — optional; when omitted the slider button is hidden.
  onOpenDrawer?: () => void;
}

export function AppTopbar({
  manifest,
  progress,
  onOpenDrawer,
  chapterNumber,
  chapterTitle,
  lessonNumber,
  lessonTitle,
}: Props): JSX.Element | null {
  const { user, isLoaded, signOut } = useAuthUser();
  const location = useLocation();

  if (!isLoaded || !user) return null;

  const email = user.email;
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ") || email;
  const role = user.role;
  // Users mid-onboarding (no completed timestamp, non-admin) get the bare
  // topbar — no drawer button, no admin/profile sub-nav.
  const notOnboarded = role !== "admin" && user.onboardingCompletedAt === null;
  const picture = "";

  const handleSignOut = (): void => {
    signOut().then(() => { window.location.href = "/signin"; });
  };

  // Progress pill math — only computed when manifest + progress are present
  const totalLessons = manifest
    ? manifest.chapters.reduce((a, c) => a + c.lessons.length, 0)
    : 0;
  const completed = progress?.completedLessons.length ?? 0;
  const percent = manifest && progress ? coursePercent(progress, totalLessons) : 0;

  const inCourse = manifest !== undefined;
  const drawerLabel = inCourse ? "Course Outline" : "Courses";
  // Show chapter/lesson breadcrumb when either a lesson number or a bare lesson
  // title is provided (chapter-quiz mode passes lessonTitle only, no number).
  const hasBreadcrumb = inCourse && (lessonNumber !== undefined || lessonTitle !== undefined);
  const hasProgress = inCourse && progress !== undefined && progress !== null;

  return (
    <div className="sticky top-0 z-20">
      <div className="bg-card border-b border-border px-3 sm:px-4 py-2 sm:py-2.5 flex items-center justify-between gap-2 sm:gap-4 shadow-sm">
        {/* LEFT — context (course or global) */}
        <div className="flex items-center gap-2 sm:gap-3.5 min-w-0 flex-1">
          {onOpenDrawer && !notOnboarded && (
            <button
              onClick={onOpenDrawer}
              className="bg-card border border-border rounded-control px-2 sm:px-3 py-1.5 cursor-pointer flex items-center gap-2 text-[13px] hover:bg-muted shrink-0"
              aria-label={`Open ${drawerLabel.toLowerCase()}`}
            >
              <span className="inline-flex flex-col gap-[3px]">
                <span className="block w-[14px] h-[2px] bg-foreground rounded-sm" />
                <span className="block w-[14px] h-[2px] bg-foreground rounded-sm" />
                <span className="block w-[14px] h-[2px] bg-foreground rounded-sm" />
              </span>
              <span className="hidden md:inline">{drawerLabel}</span>
            </button>
          )}

          {/* Breadcrumb — only shown when in-course with lesson context.
              Progressively reveals chunks as viewport widens. */}
          {inCourse && (
            <div className="flex items-baseline gap-2 text-[13px] sm:text-[14px] min-w-0">
              {/* Course title — visible from sm up */}
              <span className="hidden sm:inline font-bold text-primary tracking-tight whitespace-nowrap">
                {manifest.title}
              </span>

              {/* Chapter chunk — visible from md up */}
              {hasBreadcrumb && chapterNumber !== undefined && chapterTitle && (
                <>
                  <span className="hidden md:inline text-muted-foreground shrink-0">›</span>
                  <span className="hidden md:inline text-muted-foreground whitespace-nowrap">
                    Chapter {chapterNumber}: {chapterTitle}
                  </span>
                </>
              )}

              {/* Lesson chunk — visible at all sizes when in-course.
                  When lessonNumber is undefined (chapter-quiz mode) the title
                  renders without a "Lesson N:" prefix. */}
              {hasBreadcrumb && lessonTitle && (
                <>
                  <span className="hidden sm:inline text-muted-foreground shrink-0">›</span>
                  <span className="font-semibold text-foreground truncate">
                    {lessonNumber !== undefined && (
                      <>
                        <span className="sm:hidden">L{lessonNumber}: </span>
                        <span className="hidden sm:inline">Lesson {lessonNumber}: </span>
                      </>
                    )}
                    {lessonTitle}
                  </span>
                </>
              )}
            </div>
          )}

          {/* Progress pill — only shown when in-course with progress data */}
          {hasProgress && (
            <span className="hidden sm:inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-muted border border-border-subtle text-[11px] text-muted-foreground shrink-0">
              <span className="font-bold text-foreground">{percent}%</span>
              <span className="hidden md:inline-block w-[70px] h-[5px] rounded-full bg-border overflow-hidden">
                <span
                  className="block h-full bg-gradient-to-r from-primary to-primary-soft"
                  style={{ width: `${percent}%` }}
                />
              </span>
              <span className="hidden lg:inline">{completed} of {totalLessons}</span>
            </span>
          )}
        </div>

        {/* RIGHT — portal chrome */}
        <div className="flex items-center gap-2 sm:gap-3.5 shrink-0">
          <Link
            to="/"
            className="flex items-center gap-2.5 rounded-control px-1 py-0.5 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary/40"
            aria-label="Summer Internship home"
          >
            <span
              className="w-8 h-8 rounded-control bg-primary text-primary-foreground inline-flex items-center justify-center shadow"
              aria-hidden="true"
            >
              <CapMark className="h-5 w-5" />
            </span>
            <span className="hidden lg:inline text-[13px] font-semibold text-foreground">
              Summer Internship
            </span>
          </Link>
          <div className="flex items-center gap-2 pl-2 sm:pl-3.5 sm:border-l sm:border-border-subtle">
            <AdminChip role={role} />
            <UserMenu
              name={name}
              email={email}
              picture={picture}
              role={role}
              onSignOut={handleSignOut}
            />
          </div>
        </div>
      </div>

      {/* Admin secondary nav — only shown when on /admin path and onboarded. */}
      {!notOnboarded && location.pathname.startsWith("/admin") && (
        <SubNav
          ariaLabel="Admin"
          items={[
            { to: "/admin", label: "Users", exact: true },
            { to: "/admin/courses", label: "Courses" },
            { to: "/admin/invitations", label: "Invitations" },
            { to: "/admin/operations-guide", label: "Operations guide" },
            { to: "/admin/sitemap", label: "Sitemap" },
          ]}
        />
      )}

      {/* Profile secondary nav — shown on /profile paths for onboarded users. */}
      {!notOnboarded && location.pathname.startsWith("/profile") && (
        <SubNav
          ariaLabel="Profile"
          items={[
            { to: "/profile", label: "My Training", exact: true },
            { to: "/profile/account", label: "Account" },
            { to: "/profile/privacy", label: "Privacy" },
          ]}
        />
      )}
    </div>
  );
}
